// The day's progress — B-60's "Avance", in a dialog of its own so "Nuevo cierre" stays a short
// form. `NuevoCierre` opens it after "Procesar el día" is sent, and from the day's summary line or
// the panel's chip later.
//
// It can be closed while the day runs: the backend keeps reading the offices whether or not anyone
// watches, and `NuevoCierre` keeps polling the run it started so the panel can say when it ended.
//
// Once the run has ended it offers "Descargar Matriz del día" — the process ends by handing over
// the Matriz, as the macro's did. Same workbook as the panel's download (`matrizDelDia`), for this
// date only, and only to someone holding `cierres.export` on the client.
import { useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Info } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Spinner, cn } from "@mantis-sas/ui";

import { fechaDMY } from "../../../utils/cierrePanel";
import { nombreDeAlerta, nombreDeFuente } from "../../../utils/cierreNombres";
import { descargarMatriz } from "../../../utils/cierreExcel";
import { matrizDelDia } from "../../../utils/cierreMatrizDelDia";
import { PERMISO, puede } from "../../../utils/cierrePermisos";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { Mensaje, TONO } from "./ProcesoDelDia";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

function Contador({ etiqueta, valor, tono }) {
  return (
    <div className={cn("rounded-[10px] border px-2.5 py-1.5", TONO[tono])}>
      <p className="text-[11px] font-medium">{etiqueta}</p>
      <p className="text-[18px] font-semibold tabular-nums">{valor}</p>
    </div>
  );
}

// A run that ended with no closing at all: the calendar opened none that day. Said in the screen's
// words, because "Todavía no se ha procesado este día" right after a send reads as a failure (the
// E2E test sent Bancamía's Saturday 26/09/2026, which was not listed as worked).
const SIN_CIERRES = "No se procesó ninguna oficina: esta fecha no es día de cierre para este cliente"
  + " (fin de semana, festivo o un sábado que no está habilitado). Si sí se trabajó, hay que habilitar la fecha.";

function Avance({ avance, corriendo, enviado, nombres }) {
  if (!avance.total) {
    if (corriendo) {
      return (
        <p className="flex items-center gap-2 text-[12.5px] text-brinks-muted-foreground">
          <Spinner className="h-3.5 w-3.5" /> Preparando los cierres de las oficinas…
        </p>
      );
    }
    return enviado ? <Mensaje tono="aviso" lineas={SIN_CIERRES} /> : (
      <p className="text-[12.5px] text-brinks-muted-foreground">Todavía no se ha procesado este día.</p>
    );
  }
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[12.5px] text-brinks-muted-foreground">
          <span>
            {avance.leidas + avance.fallidas} de {avance.total} oficinas
          </span>
          <span className="font-semibold tabular-nums text-brinks-foreground">{avance.porcentaje}%</span>
        </div>
        <div
          className="h-2 overflow-hidden rounded-full bg-brinks-surface"
          role="progressbar"
          aria-label="Avance del día"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={avance.porcentaje}
        >
          <div className={cn("h-full rounded-full transition-all", avance.fallidas ? "bg-brinks-warning" : "bg-brinks-success")} style={{ width: `${avance.porcentaje}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        <Contador etiqueta="Oficinas" valor={avance.total} tono="neutral" />
        <Contador etiqueta="Leídas" valor={avance.leidas} tono="ok" />
        <Contador etiqueta="Con error" valor={avance.fallidas} tono={avance.fallidas ? "error" : "neutral"} />
        <Contador etiqueta="Pendientes" valor={avance.pendientes} tono="neutral" />
      </div>

      {avance.conProblema.length ? (
        <div className="space-y-1.5">
          <h3 className="flex items-center gap-1.5 text-[13px] font-semibold text-brinks-foreground">
            <AlertTriangle className="h-4 w-4 text-brinks-warning" /> Oficinas con problema
          </h3>
          <ul className="max-h-48 divide-y overflow-y-auto rounded-[10px] border">
            {avance.conProblema.map((oficina) => (
              <li key={oficina.closingId} className="px-3 py-1.5 text-[12.5px]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium tabular-nums">Oficina {oficina.officeCode}</span>
                  <span className={cn("rounded-full border px-2 py-0.5 text-[11px]", TONO[oficina.tipo === "error" ? "error" : "aviso"])}>
                    {oficina.tipo === "error" ? "No se pudo leer" : "Sin movimientos en el archivo"}:{" "}
                    {oficina.claves.map((clave) => nombreDeFuente(nombres, clave)).join(", ")}
                  </span>
                </div>
                {/* B-126 — why, and what to do about it. */}
                {(oficina.motivos || []).map((motivo) => (
                  <p key={motivo.mensaje} className="mt-1 text-[11.5px] leading-snug text-brinks-muted-foreground">
                    {motivo.mensaje} <span className="text-brinks-foreground">{motivo.accion}</span>
                  </p>
                ))}
              </li>
            ))}
          </ul>
          {avance.conProblema.some((oficina) => oficina.tipo === "sinFilas") ? (
            <p className="text-[12px] text-brinks-muted-foreground">
              "Sin movimientos" puede ser un día sin operación, o un código de oficina que no coincide con el del reporte.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function AvanceDelDiaDialog({ open, onOpenChange, dia, nombres, cliente, token, mantisBusinessId, clientCode }) {
  const { corriendo, avance } = dia;
  const fecha = dia.fecha ? fechaDMY(dia.fecha) : "";
  const acceso = useAccesoCierres();
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState("");

  // Offered once the run has ended and read at least one office — not while it runs (the Matriz
  // would miss the offices still being read), not for a date that opened no closing.
  const puedeDescargar = !corriendo && avance.total > 0 && !!clientCode && !!dia.fecha && puede(acceso, PERMISO.EXPORTAR, { cliente: clientCode });

  const descargar = async () => {
    setDescargando(true);
    setErrorDescarga("");
    try {
      const hoja = await matrizDelDia({
        token,
        mantisBusinessId,
        clientCode,
        fecha: dia.fecha,
        acceso,
        nombrar: (clave) => nombreDeAlerta(nombres, clave),
      });
      await descargarMatriz(hoja);
    } catch (e) {
      console.error("No se pudo generar la Matriz del día", e);
      setErrorDescarga("No se pudo generar la Matriz del día. Intenta de nuevo, o descárgala desde el panel.");
    } finally {
      setDescargando(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="brinks-scope font-redhat flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-y-auto p-0">
        <DialogHeader className="border-b p-4 text-left">
          <div className="flex items-center gap-3">
            <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-[8px]", TONO[corriendo ? "neutral" : avance.fallidas || avance.pendientes ? "aviso" : "ok"])}>
              {corriendo ? <Spinner className="h-4 w-4" /> : avance.fallidas || avance.pendientes ? <AlertTriangle className="h-[18px] w-[18px]" /> : <CheckCircle2 className="h-[18px] w-[18px]" />}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">{corriendo ? "Procesando el día" : !avance.total ? (dia.enviado ? "Ninguna oficina para procesar" : "Avance del día") : avance.pendientes ? "Proceso incompleto" : avance.fallidas ? "Día procesado con errores" : "Día procesado"}</DialogTitle>
                <BotonGuia guiaId="dialogo-avance-del-dia" titulo="Avance del día" texto="Cómo va el proceso del día: cuántas oficinas se leyeron y cuáles tuvieron un problema. El proceso sigue en el servidor aunque cierres esta ventana." nombre="Avance del día" />
              </div>
              <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
                {[cliente, fecha].filter(Boolean).join(" · ")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 p-4">
          {dia.errorEnvio ? <Mensaje tono="aviso" lineas={dia.errorEnvio} /> : null}
          {/* From the 202 until the run ends. Once it ends the title says so and this would contradict it. */}
          {dia.enviado && (corriendo || dia.esperandoAvance) ? (
            <Mensaje tono="ok" lineas="Reportes recibidos. Las oficinas se están procesando en el servidor." />
          ) : null}
          {dia.reintentando ? (
            <Mensaje tono="aviso" lineas="No pudimos actualizar el avance. El proceso sigue en el servidor; reintentando…" />
          ) : null}
          <Mensaje lineas={dia.errorCarga} />

          <div {...guia("Avance", "Las oficinas leídas sobre el total y la barra de avance. Debajo, solo las oficinas con un problema y por qué.")}>
            <Avance avance={avance} corriendo={corriendo} enviado={dia.enviado} nombres={nombres} />
          </div>

          {corriendo ? (
            <div role="note" className="flex gap-2 rounded-[8px] border border-brinks-border bg-brinks-surface px-2.5 py-2 text-[12.5px] text-brinks-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-brinks-muted-foreground" />
              <p>
                <span className="font-semibold">Puedes cerrar esta ventana.</span> El proceso sigue en el servidor y te avisaremos en el panel cuando termine.
              </p>
            </div>
          ) : null}
          <Mensaje lineas={errorDescarga} />
        </div>

        <DialogFooter className="gap-2 border-t p-3">
          {puedeDescargar ? (
            <Button {...guia("Descargar Matriz del día", "Cuando el proceso termina, descarga la Matriz de ese día en Excel, como la que se envía al cliente.")} className="w-full gap-1.5 sm:w-auto" variant="outline" onClick={descargar} disabled={descargando}>
              {descargando ? <Spinner className="h-4 w-4" /> : <Download className="h-4 w-4" />}
              {descargando ? "Generando Matriz…" : "Descargar Matriz del día"}
            </Button>
          ) : null}
          <Button className="w-full sm:w-auto" variant={corriendo ? "outline" : "default"} onClick={() => onOpenChange(false)}>
            {corriendo ? "Seguir en segundo plano" : "Listo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
